import React from "react";
import { Building2, Truck, Snowflake, Wrench, Shield, FileText } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import FormWithWhatsApp from "@/components/FormWithWhatsApp";
import { buildWaLink } from "@/lib/whatsapp";

const PILLARS = [
  {
    id: "medicines",
    title: "Medicines",
    body: "Dewormers, antibiotics, supplements, biosecurity and dairy support across the BAH catalogue.",
    Icon: FileText,
  },
  {
    id: "instruments",
    title: "Instruments",
    body: "BAH Instruments Catalogue 2026 — castration, dosing, hoof, calving, milking and small-animal tools.",
    Icon: Wrench,
  },
  {
    id: "cold-chain",
    title: "Cold-chain / Vaccine carriers",
    body: "Haier vaccine refrigerators, carriers and electronic temperature loggers for institutional cold-chain.",
    Icon: Snowflake,
  },
  {
    id: "biosecurity",
    title: "Biosecurity",
    body: "OMNICIDE, Kleenol, Hycolin, Biodine and hand-hygiene products for institutions and supply chains.",
    Icon: Shield,
  },
  {
    id: "supply",
    title: "Institutional supply",
    body: "Programme-scale supply for institutions, projects, NGOs and government programmes.",
    Icon: Truck,
  },
  {
    id: "tenders",
    title: "Tenders & supply inquiries",
    body: "Submit tender opportunities and supply requests — the BAH team will respond with quotes and specifications.",
    Icon: Building2,
  },
];

export default function Distribution() {
  return (
    <div data-testid="distribution-page">
      <PageHero
        testId="distribution-hero"
        eyebrow="Distribution / Institutional"
        title="Programme-scale animal health supply."
        lede="For distributors, institutions, NGOs, government programmes and large-scale buyers — medicines, instruments, cold-chain and biosecurity, supplied together."
        image="https://images.pexels.com/photos/735968/pexels-photo-735968.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{ label: "Submit Inquiry Below", href: "#distribution-form" }}
        secondaryCta={{
          label: "WhatsApp Partnerships",
          href: buildWaLink("Hello Biodeal Animal Health, I am a distributor / institution and would like to discuss a supply partnership."),
          external: true,
        }}
      />

      <section className="py-20 md:py-28 bg-bah-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <SectionLabel>Range available</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
              A complete animal health ecosystem.
            </h2>
            <p className="mt-4 text-bah-mute md:text-lg">
              Combining medicines, instruments, cold-chain and biosecurity — practical for East African field conditions.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p) => (
              <div
                key={p.id}
                className="card-rise bg-white border border-bah-line rounded-2xl p-6"
                data-testid={`dist-pillar-${p.id}`}
              >
                <div className="h-11 w-11 rounded-xl bg-bah-green/10 text-bah-green grid place-content-center">
                  <p.Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-xl text-bah-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-bah-mute leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="distribution-form"
        className="py-20 md:py-28 bg-bah-paperAlt scroll-mt-28"
        data-testid="dist-form-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel>Submit inquiry</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
              Talk to Biodeal Animal Health about distribution or institutional supply.
            </h2>
            <p className="mt-4 text-bah-mute">
              Share details of your organisation and the supply requirement — the BAH commercial team will respond with the right next step.
            </p>
          </div>
          <div className="lg:col-span-7">
            <FormWithWhatsApp
              endpoint="/forms/distribution"
              testidPrefix="distribution"
              submitLabel="Submit Institutional Inquiry"
              successText="We've received your distribution inquiry. The Biodeal Animal Health commercial team will be in touch."
              aiDraft={{ topic: "distribution", targetField: "message", roleField: "role" }}
              waMessageBuilder={(v) =>
                `Hello Biodeal Animal Health, distribution / institutional inquiry — ${v.organization || "[organization]"} (${v.role || "role"}) in ${v.location || "[location]"}${
                  v.country ? `, ${v.country}` : ""
                }. Type: ${v.inquiry_type || "[type]"}. ${v.volume_estimate ? `Volume: ${v.volume_estimate}. ` : ""}${v.message || ""}`
              }
              fields={[
                { name: "organization", label: "Organisation", required: true, placeholder: "Distributor, NGO, government dept, institution…" },
                { name: "contact_person", label: "Contact person", required: true, placeholder: "Full name" },
                { name: "role", label: "Your role", placeholder: "e.g. Procurement Officer" },
                { name: "country", label: "Country", placeholder: "Kenya, Uganda, Tanzania…" },
                { name: "location", label: "Location", required: true, placeholder: "City / county" },
                { name: "phone", label: "Phone / WhatsApp", required: true, placeholder: "+254…" },
                { name: "email", label: "Email", type: "email" },
                {
                  name: "inquiry_type",
                  label: "Inquiry type",
                  required: true,
                  options: [
                    "Medicines",
                    "Instruments",
                    "Cold Chain / Vaccine Carriers",
                    "Biosecurity",
                    "Tender / Supply",
                    "Institutional Support",
                    "Other",
                  ],
                },
                { name: "volume_estimate", label: "Volume / scale estimate", placeholder: "e.g. 500 units / month" },
                {
                  name: "message",
                  label: "Message",
                  required: true,
                  textarea: true,
                  rows: 4,
                  full: true,
                  placeholder: "Programme details, tender reference numbers, timelines, target geographies…",
                },
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
